import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ConversationMessageEntity } from './conversation-message.entity';
import { ConversationToolUsageEntityReferenceEntity } from './conversation-tool-usage-entity-reference.entity';

/**
 * PD-16903 added REFUSED (the caller's access does not allow the call, as the
 * matching screen would refuse it) and NEEDS_INPUT (the call lacks something
 * only the user can supply). Neither is a failure: the agent reads the plain
 * reason, stored in `errorMessage`, back to the user. The column is a MySQL
 * ENUM, so the values were added by the instance migration
 * `AddToolUsageRefusedNeedsInputStatuses`. SM1 never reads this table.
 */
export enum ConversationToolUsageStatus {
	PENDING = 'pending',
	SUCCESS = 'success',
	ERROR = 'error',
	REFUSED = 'refused',
	NEEDS_INPUT = 'needs_input',
}

@Entity('_conversation_tool_usage')
@Index(['messageId'])
@Index(['status'])
@Index(['startedAt'])
@Index(['toolUsageUuid'])
export class ConversationToolUsageEntity extends BaseEntity {
	@Column({
		name: 'tool_usage_uuid',
		type: 'varchar',
		length: 36,
		nullable: true,
		comment:
			'UUID for correlating create/update operations across server reconnections',
	})
	public toolUsageUuid?: string;

	@Column('int', {
		name: '_fk_conversation_message__id',
		unsigned: true,
		nullable: true,
	})
	public messageId?: number;

	@ManyToOne(() => ConversationMessageEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'CASCADE',
		nullable: true,
	})
	@JoinColumn([
		{ name: '_fk_conversation_message__id', referencedColumnName: 'id' },
	])
	public message?: ConversationMessageEntity;

	@Column({
		name: 'tool_name',
		type: 'varchar',
		length: 100,
		nullable: false,
		comment: 'Name of the tool that was executed',
	})
	public toolName!: string;

	@Column({
		name: 'tool_type',
		type: 'varchar',
		length: 100,
		nullable: false,
		comment: 'Type of the tool that was executed',
	})
	public toolType!: string;

	@Column({
		name: 'input_data',
		type: 'json',
		nullable: true,
		comment: 'Input arguments passed to the tool',
	})
	public inputData?: any;

	@Column({
		name: 'output_data',
		type: 'json',
		nullable: true,
		comment: 'Output data returned by the tool',
	})
	public outputData?: any;

	@Column({
		name: 'formatted_output',
		type: 'text',
		nullable: true,
		comment:
			'Formatted output string sent to LLM (includes speech formatting, TOOL RESULT prefix)',
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public formattedOutput?: string;

	@Column({
		name: 'error_message',
		type: 'text',
		nullable: true,
		comment: 'Error message if tool execution failed',
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public errorMessage?: string;

	@Column({
		name: 'error_details',
		type: 'json',
		nullable: true,
		comment: 'Detailed error information including stack trace',
	})
	public errorDetails?: any;

	@Column({
		name: 'status',
		type: 'enum',
		enum: ConversationToolUsageStatus,
		nullable: false,
		comment: 'Current status of the tool execution',
	})
	public status!: ConversationToolUsageStatus;

	@Column({
		name: 'execution_time_ms',
		type: 'int',
		nullable: true,
		comment: 'Time taken for tool execution in milliseconds',
	})
	public executionTimeMs?: number;

	@Column({
		name: 'started_at',
		type: 'datetime',
		nullable: false,
		comment: 'When the tool execution started',
	})
	public startedAt!: Date;

	@Column({
		name: 'completed_at',
		type: 'datetime',
		nullable: true,
		comment: 'When the tool execution completed',
	})
	public completedAt?: Date;

	@OneToMany(
		() => ConversationToolUsageEntityReferenceEntity,
		(reference) => reference.toolUsage,
	)
	public entityReferences?: ConversationToolUsageEntityReferenceEntity[];
}
